import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useVoice } from '../context/VoiceContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, Volume2, Loader2, StopCircle, Trash2, Send, Globe } from 'lucide-react';
import { GlassCard } from '../components/layout/GlassCard';
import { AIBrainVisualizer } from '../components/ui/AIVoiceVisualizer';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VoiceAgentPage() {
  const { token } = useAuth();
  const { 
    voiceEnabled, 
    speak, 
    stopSpeaking, 
    isSpeaking,
    language,
    autoDetectLanguage,
    setDetectedLanguage,
    getSpeechRecognitionLanguage,
    getCurrentLanguage,
    SUPPORTED_LANGUAGES,
    wakeWordDetected
  } = useVoice();
  const [visualizerState, setVisualizerState] = useState('idle');
  const [conversation, setConversation] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [inputText, setInputText] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [currentDetectedLang, setCurrentDetectedLang] = useState(null);
  const visualizerRef = useRef(null);
  const recognitionRef = useRef(null);

  // Listen for wake word detection and auto-start recording
  useEffect(() => {
    if (wakeWordDetected && !isProcessing && visualizerState === 'idle') {
      // Small delay to let the wake word animation complete
      setTimeout(() => {
        startRecording();
      }, 500);
    }
  }, [wakeWordDetected, isProcessing, visualizerState]);

  // Initialize Web Speech Recognition with language support
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      
      // Set language based on settings
      const langCode = getSpeechRecognitionLanguage();
      recognitionRef.current.lang = langCode;
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [getSpeechRecognitionLanguage]);

  // Update recognition language when settings change
  useEffect(() => {
    if (recognitionRef.current) {
      const langCode = getSpeechRecognitionLanguage();
      recognitionRef.current.lang = langCode;
    }
  }, [language, getSpeechRecognitionLanguage]);

  // Start voice recording using Web Speech API
  const startRecording = useCallback(async () => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }

    try {
      recognitionRef.current.onstart = () => {
        setVisualizerState('listening');
        setInterimTranscript('');
        if (visualizerRef.current) {
          visualizerRef.current.startListening();
        }
      };

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interim = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interim += transcript;
          }
        }
        
        setInterimTranscript(interim);
        
        if (finalTranscript) {
          processVoiceInput(finalTranscript);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'no-speech') {
          toast.error('No speech detected. Please try again.');
        } else if (event.error === 'audio-capture') {
          toast.error('Microphone not found or not accessible.');
        } else if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please allow microphone access.');
        }
        setVisualizerState('idle');
        setInterimTranscript('');
      };

      recognitionRef.current.onend = () => {
        if (visualizerState === 'listening') {
          setVisualizerState('idle');
        }
        if (visualizerRef.current) {
          visualizerRef.current.stopListening();
        }
      };

      recognitionRef.current.start();
    } catch (error) {
      console.error('Start recording error:', error);
      toast.error('Could not start voice recording');
      setVisualizerState('idle');
    }
  }, [visualizerState]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    if (visualizerRef.current) {
      visualizerRef.current.stopListening();
    }
  }, []);

  // Process voice input
  const processVoiceInput = async (userText) => {
    if (!userText.trim()) return;
    
    setIsProcessing(true);
    setVisualizerState('thinking');
    setInterimTranscript('');

    // Add user message to conversation
    setConversation(prev => [...prev, { role: 'user', content: userText }]);

    try {
      // Get current language for response
      const langConfig = getCurrentLanguage();
      
      // Get AI response with language context
      const chatResponse = await axios.post(
        `${API_URL}/api/chat`,
        { 
          message: userText, 
          module: 'voice',
          language: langConfig.id,
          respond_in_same_language: true
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );

      const aiResponse = chatResponse.data.response;
      
      // Detect language from response if auto-detect is enabled
      if (autoDetectLanguage && chatResponse.data.detected_language) {
        setDetectedLanguage(chatResponse.data.detected_language);
        setCurrentDetectedLang(chatResponse.data.detected_language);
      }
      
      setConversation(prev => [...prev, { role: 'assistant', content: aiResponse }]);

      // Speak the response using browser TTS
      if (voiceEnabled) {
        setVisualizerState('speaking');
        try {
          await speak(aiResponse);
        } catch (e) {
          console.log('TTS not available');
        }
      }
      
      setVisualizerState('idle');
    } catch (error) {
      toast.error('Failed to get response');
      console.error(error);
      setVisualizerState('idle');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle text submit
  const handleTextSubmit = (e) => {
    e.preventDefault();
    if (inputText.trim()) {
      processVoiceInput(inputText);
      setInputText('');
    }
  };

  const handleStopSpeaking = () => {
    stopSpeaking();
    setVisualizerState('idle');
  };

  const handleVisualizerClick = () => {
    if (isProcessing) return;
    
    if (visualizerState === 'listening') {
      stopRecording();
    } else if (visualizerState === 'speaking') {
      handleStopSpeaking();
    } else if (visualizerState === 'idle') {
      startRecording();
    }
  };

  const clearConversation = () => {
    setConversation([]);
    toast.success('Conversation cleared');
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="voice-agent-page">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">AIRA Voice Agent</h1>
        <p className="text-gray-400">Have natural voice conversations with your AI Brain</p>
        
        {/* Current Language Indicator */}
        <div className="mt-4 flex items-center justify-center gap-2">
          <Globe className="w-4 h-4 text-accent" />
          <span className="text-sm text-accent">
            {getCurrentLanguage().flag} {getCurrentLanguage().nativeName}
            {autoDetectLanguage && currentDetectedLang && (
              <span className="text-gray-500 ml-2">(Auto-detected)</span>
            )}
          </span>
        </div>
      </div>

      {/* Main Voice Interface */}
      <GlassCard className="p-8 mb-6">
        <div className="flex flex-col items-center">
          {/* AI Voice Visualizer */}
          <motion.div
            whileHover={{ scale: isProcessing ? 1 : 1.02 }}
            whileTap={{ scale: isProcessing ? 1 : 0.98 }}
            onClick={handleVisualizerClick}
            className={`cursor-pointer ${isProcessing && visualizerState === 'thinking' ? 'pointer-events-none' : ''}`}
            data-testid="voice-visualizer"
          >
            <AIBrainVisualizer
              ref={visualizerRef}
              mode="interactive"
              size="lg"
              state={visualizerState}
              isActive={true}
              showLabel={false}
            />
          </motion.div>

          {/* Interim transcript display */}
          {interimTranscript && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 px-4 py-2 bg-primary/10 border border-primary/30 rounded-full"
            >
              <p className="text-primary text-sm italic">{interimTranscript}...</p>
            </motion.div>
          )}

          {/* Quick Actions */}
          <div className="flex items-center gap-4 mt-8">
            {visualizerState === 'speaking' && (
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={handleStopSpeaking}
                className="flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-full text-red-400 hover:bg-red-500/30 transition-colors"
                data-testid="stop-speaking-btn"
              >
                <StopCircle className="w-4 h-4" />
                Stop Speaking
              </motion.button>
            )}
            
            {visualizerState === 'listening' && (
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={stopRecording}
                className="flex items-center gap-2 px-4 py-2 bg-accent/20 border border-accent/30 rounded-full text-accent hover:bg-accent/30 transition-colors"
                data-testid="stop-recording-btn"
              >
                <MicOff className="w-4 h-4" />
                Stop Recording
              </motion.button>
            )}
          </div>

          {/* Instructions */}
          <motion.p 
            className="mt-6 text-gray-500 text-sm text-center max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {visualizerState === 'idle' && 'Click the AI brain to start speaking with AIRA'}
            {visualizerState === 'listening' && 'Speak naturally... I\'m listening'}
            {visualizerState === 'thinking' && 'AIRA is processing your message...'}
            {visualizerState === 'speaking' && 'AIRA is responding to you'}
          </motion.p>
        </div>
      </GlassCard>

      {/* Text Input Alternative */}
      <form onSubmit={handleTextSubmit} className="mb-6">
        <div className="relative">
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Or type your message here..."
            className="input-field w-full pr-24 py-4"
            disabled={isProcessing}
            data-testid="voice-text-input"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
            <button
              type="button"
              onClick={startRecording}
              disabled={isProcessing || visualizerState === 'listening'}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <Mic className="w-5 h-5 text-gray-400" />
            </button>
            <button
              type="submit"
              disabled={isProcessing || !inputText.trim()}
              className="p-2 bg-primary text-white rounded-lg disabled:opacity-50"
            >
              {isProcessing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </form>

      {/* Conversation History */}
      <GlassCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold">Conversation</h3>
          {conversation.length > 0 && (
            <button
              onClick={clearConversation}
              className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
              title="Clear conversation"
            >
              <Trash2 className="w-4 h-4 text-red-400" />
            </button>
          )}
        </div>
        
        <div className="space-y-4 max-h-96 overflow-y-auto custom-scrollbar">
          {conversation.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 flex items-center justify-center mx-auto mb-4">
                <Mic className="w-8 h-8 text-primary" />
              </div>
              <p className="text-gray-500">Your voice conversations will appear here</p>
              <p className="text-gray-600 text-sm mt-2">Click the visualizer above to start</p>
            </div>
          ) : (
            <AnimatePresence>
              {conversation.map((msg, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex items-start gap-3 max-w-[80%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === 'user' 
                        ? 'bg-gradient-to-r from-primary to-secondary'
                        : 'bg-gradient-to-r from-accent/20 to-primary/20 border border-accent/30'
                    }`}>
                      {msg.role === 'user' ? (
                        <Mic className="w-4 h-4 text-white" />
                      ) : (
                        <Volume2 className="w-4 h-4 text-accent" />
                      )}
                    </div>
                    <div className={msg.role === 'user' ? 'chat-message-user' : 'chat-message-ai'}>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
          
          {/* Processing indicator */}
          {isProcessing && visualizerState === 'thinking' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-accent/20 to-primary/20 border border-accent/30 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 text-accent animate-spin" />
                </div>
                <div className="chat-message-ai flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-gray-400">AIRA is thinking...</span>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </GlassCard>

      {/* Voice Tips */}
      <motion.div 
        className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-xl"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <p className="text-primary/80 text-sm">
          <strong>Tip:</strong> Speak clearly and naturally. AIRA uses real-time speech recognition 
          and will respond with voice. Adjust voice settings in the Settings page.
        </p>
      </motion.div>
    </div>
  );
}
